import type { RealTimeDataPacket } from "./packet.worker";
import { splitMultiMotorPlotKey } from "./packet.worker";
import { TelemValue, TelemIdxType } from "cstore/telem";
import { PacketId, displayUnits, IMultiMotorTelemetryMessage } from "./common-types";

export type SlntFile = SlntFileV2;

////////// V1 below ///////////
// Provided as documentation of the 2023-2025 "v1" format (though we did
// not use version numbers at the time).
export type SlntFileV1 = RealTimeDataPacket[];

////////// V2 below ///////////
export type SlntFileV2 = FileItem[];

// Note that every FileItem object must have an "id" field, and that the number
// 128 (PacketId.REALTIME_DATA) must _only_ be used for the first variant, for
// backwards compatibility reasons. Also, every FileItem object must have a
// "packet" field whose value is an object. The V2 variants all have the empty
// object as their "packet" values.
//
// Additionally, all the V2 format IDs should be strings.
export type FileItem = RealTimeDataPacket | SlntHeader | VariableTelemSamples | MultiMotorTelemSamples;

export type FieldMetadata = {
    // Needed for variable telemetry
    units?: string;
};

export type TelemSetElement = { fieldName: string, fieldType: TelemIdxType };

export type SlntHeader = {
    id: "header";
    // initial version=1
    version: number;
    // telemSet is what gets supplied to loadTelemMap in packet-workers.ts
    telemSet: TelemSetElement[];
    // multiMotorTelemSet describes multi-motor telemetry fields for PSCB
    multiMotorTelemSet?: TelemSetElement[];
    // subActuators provides information about the actuators connected to the PSCB
    subActuators?: SubActuators;

    fieldsMetadata: { [ fieldName: string ]: FieldMetadata },

    // Unused; present for backwards compatibility
    packet: unknown; // Must be created as {}
};

// One or more variable telemetry samples. It is invalid to have empty array
// for `a` key. Note: key names are intentionally shortened for data
// efficiency.
export type VariableTelemSamples = {
    id: "vt1"; // vt1 = Variable Telemetry version 1
    o: number; // o = offset of array `a` into telemSet in SlntHeader
    a: TelemValue[]; // a[0] is value for telemSet[o+0], a[1] is value for telemSet[o+1], etc.
    ts: number; // Timestamp in ms

    // Unused; present for backwards compatibility
    packet: unknown; // Must be created as {}
};

/// The following section is for connections to PSCB (Power Supply Control Board).
export type SubActuators = { [nodeId: number]: ActuatorInfo };

export type ActuatorInfo = {
    nodeId: number;
    disposition: string;
};

// One or more multi-motor telemetry samples. Same structure as VariableTelemSamples
// but indexes into multiMotorTelemSet in SlntHeader.
export type MultiMotorTelemSamples = {
    id: "mmt1"; // mmt1 = Multi-Motor Telemetry version 1
    o: number; // o = offset into multiMotorTelemSet in SlntHeader
    a: TelemValue[]; // parallel values
    ts: number; // Timestamp in ms

    // Unused; present for backwards compatibility
    packet: unknown; // Must be created as {}
};

////////// Access logic below ///////////

// This is the same as one element of telemSet but with FieldMetadata included.
// Generally this will be from getTelemSet() but with units from the telem.toml added.
export type TelemInfo = TelemSetElement & {
    units?: string;
};

export const makeSlntHeader = (
    telemInfo: TelemInfo[],
    multiMotorTelemSet?: TelemSetElement[],
    subActuators?: SubActuators,
): SlntHeader => {
    const telemSet = telemInfo.map( el => ( { fieldName: el.fieldName, fieldType: el.fieldType } ) ); // omit units

    let fieldsMetadata: { [ fieldName: string ]: FieldMetadata } = {};
    for ( const el of telemInfo ) {
        fieldsMetadata[ el.fieldName ] = { units: el.units };
    }
    for ( const [plotKey, units] of Object.entries( displayUnits[ PacketId.REALTIME_DATA ] ) ) {
        fieldsMetadata[ plotKey ] = { units };
    }
    if ( multiMotorTelemSet ) {
        for ( let i = 0; i < multiMotorTelemSet.length; i++ ) {
            const plotKey = multiMotorTelemSet[i].fieldName;
            const fieldName = splitMultiMotorPlotKey( plotKey )?.baseName;
            if ( !fieldName ) continue;
            const units = displayUnits[ PacketId.MULTI_MOTOR_TELEMETRY ][ fieldName as keyof IMultiMotorTelemetryMessage ];
            if ( units === undefined ) continue;
            fieldsMetadata[ plotKey ] = { units };
        }
    }

    let header: SlntHeader = {
        id: "header",
        version: 1,
        telemSet,
        fieldsMetadata,
        packet: {},
    };
    if ( multiMotorTelemSet ) header.multiMotorTelemSet = multiMotorTelemSet;
    if ( subActuators ) header.subActuators = { ...subActuators };
    return header;
};

// Deserialize the contents of an SLNT file (Format V1 or V2) into an SlntFile
// type (array of elements).
export const slntParse = ( fileContents: string ): SlntFile => {
    return JSON.parse( fileContents );
};

// If this is V2 format, then return it. Otherwise, return undefined.
// This works by checking the first element to see if id == "header"
export const trySlntFormatV2 = ( slnt: SlntFile ): SlntFileV2 | undefined => {
    if ( slnt?.length === 0 || slnt[0].id !== "header" ) return undefined;
    // ToDo: it may be prudent to do other checks here.
    return slnt as SlntFileV2;
}

// Get the header for V2 files.
export const getSlntHeader = ( slntV2: SlntFileV2 ): SlntHeader => {
    const item = slntV2[0];
    if ( !item || item.id !== "header" ) throw new Error( "Missing header in SLNT format V2 file" );
    return item as SlntHeader;
}

export const extractIPacketsFromSlnt = ( slntFile: SlntFile ): RealTimeDataPacket[] => {
    // This makes the assumption that any numeric ID is an IPacket.
    return slntFile.filter( (fileItem) => isFinite( fileItem.id as number ) ) as RealTimeDataPacket[];
};

export const extractVariableTelemSamplesFromSlnt = ( slntFile: SlntFileV2 ): VariableTelemSamples[] => {
    return slntFile.filter( (fileItem) => fileItem.id === "vt1" ) as VariableTelemSamples[];
};

export const extractMultiMotorTelemSamplesFromSlnt = ( slntFile: SlntFileV2 ): MultiMotorTelemSamples[] =>
    slntFile.filter( (item) => item.id === "mmt1" ) as MultiMotorTelemSamples[];

const DEFAULT_TS_PRECISION = 1;

export type SlntStringifyOptions = {
    // For timestamps: number of digits after the decimal point to save in the
    // SLNT file. Defaults to DEFAULT_TS_PRECISION.
    tsPrecision?: number;
};

const roundLegacyTelemetryToFixed: { [ k: string ]: number } = {
  motorSpeed:            2,
  motorCurrent:          4,
  batteryVoltage:        4,
  batteryCurrent:        4,
  servoPulse:            4,
  inverterTemp:          3,
  motorPosition:         4,
  positionSensorReading: 4,
  externalThermTemp:     3,
  rawPositionSensor:     4,
};
const VAR_TELEM_ROUNDING_PLACES = 4;

const _roundFloatRE1 = /0+$/;
const _roundFloatRE2 = /\.$/;

const roundFloat = ( n: number, places: number ): string => {
    const valStr = n.toFixed(places).toString();
    if ( valStr.indexOf(".") !== -1 ) {
        // Remove trailing zeroes (and possibly decimal point)
        return valStr.replace( _roundFloatRE1, "" ).replace( _roundFloatRE2, "")
    }
    return valStr;
};

// Export the "packet" object of the Format V1 IPacket structures. A specific
// set of fields has custom rounding, and the rest are left unrounded.
const slntStringifyLegacyPacket = ( packetObj: { [ k: string ]: number } ): string => {
    let parts = [];
    for ( const key of Object.keys( packetObj ) ) {
        const keyJson = JSON.stringify(key);
        const value = packetObj[key];
        const places = roundLegacyTelemetryToFixed[ key ];
        const valueJson = ( places !== undefined ) ? roundFloat( value, places ) : JSON.stringify(value);
        parts.push( keyJson + ':' + valueJson );
    }
    return "{" + parts.join(",") + "}";
}

const slntStringifyFileItem = ( fileItem: FileItem, options: SlntStringifyOptions = {} ): string => {
    const tsPrecision = options.tsPrecision ?? DEFAULT_TS_PRECISION;
    let parts = [];
    for ( const key of Object.keys( fileItem ) ) {
        const keyJson = JSON.stringify(key);
        const value = (fileItem as {[s:string]:unknown})[key];

        let valueJson;
        if ( ( key === "ts" || key === "timestampMs" ) && typeof value === "number" ) {
            valueJson = value.toFixed( tsPrecision );
        } else if ( key === "packet" ) {
            // Legacy packet handling
            valueJson = slntStringifyLegacyPacket( value as { [ k: string ]: number } );
        } else if ( key === "a" && value instanceof Array ) {
            const elements = value.map( v => typeof v === "number" ? roundFloat( v, VAR_TELEM_ROUNDING_PLACES ) : JSON.stringify( v ) );
            valueJson = "[" + elements.join(",") + "]";
        } else {
            valueJson = JSON.stringify( value );
        }

        parts.push( keyJson + ':' + valueJson );
    }
    return "{" + parts.join(",") + "}";
};

export const slntStringify = ( slntFile: SlntFileV2, options: SlntStringifyOptions = {} ): string => {
    let parts = ["["];

    for ( let i = 0; i < slntFile.length; i++ ) {
        const fileItem = slntFile[i];
        let s = slntStringifyFileItem( fileItem, options );
        if ( i < slntFile.length - 1 ) s += ",";
        parts.push( s );
    }

    parts.push("]");
    parts.push("");
    return parts.join("\n");
}
